import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Copy, CheckCircle2, Eye, EyeOff, Bookmark, ChevronLeft, ChevronRight,
    Globe, User, Key, Monitor, MapPin, Shield, FileText, Calendar, Database, Lock, Loader2, AlertTriangle, Terminal, Network
} from 'lucide-react';
import { PriorityBadge, DataTypeBadge } from '@/components/common/ui/PriorityStatusBadges';
import { useToast } from '@/hooks/components/common/toast/useToast';
import { useUnmaskSearchPasswordMutation } from '@/lib/services/unmaskApi';

// Re-using synced types to prevent interface mismatch errors
type PriorityLevel = 'critical' | 'high' | 'medium' | 'low';

interface SearchResult {
    data_type?: string | null;
    url?: string | null;
    log_id?: number | null;
    username?: string | null;
    password?: string | null;
    log_date?: string | null;
    hostname?: string | null;
    ip?: string | null;
    malware_path?: string | null;
    anti_virus?: string | null;
    _source_endpoint?: string | null;
    country_code?: string | null;
    country?: string | null;
    priority?: PriorityLevel;
    originalIndex: number;
}

interface SearchResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    threat: SearchResult | null;
    threats: SearchResult[];
    currentIndex: number;
    onNavigate?: (index: number) => void;
    canSaveIncidents?: boolean;
    onSaveToThreats?: (threat: SearchResult) => void;
    fetchId?: string;
    context?: 'search' | 'saved';
}

const SearchResultModal: React.FC<SearchResultModalProps> = ({
    isOpen,
    onClose,
    threat,
    threats,
    currentIndex,
    onNavigate,
    onSaveToThreats,
    fetchId,
    context = 'search'
}) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [unmaskedPassword, setUnmaskedPassword] = useState<string | null>(null);
    const [unmaskError, setUnmaskError] = useState<string | null>(null);
    const [remainingUnmasks, setRemainingUnmasks] = useState<number | null>(null);
    const [unmaskSource, setUnmaskSource] = useState<string | null>(null);
    const [wasAlreadyUnmasked, setWasAlreadyUnmasked] = useState<boolean>(false);
    const [isSavingRecord, setIsSavingRecord] = useState(false); 
    const modalRef = useRef<HTMLDivElement>(null);
    const toast = useToast();

    const [unmaskSearchPassword, { isLoading: isUnmasking }] = useUnmaskSearchPasswordMutation();

    const getPriority = (dataType: string | null | undefined): PriorityLevel => {
        if (!dataType) return 'medium';
        return dataType.toLowerCase() === 'stealer' ? 'high' : 'medium';
    };

    const formatDataType = (dataType: string | null | undefined): string => {
        if (!dataType) return 'Unknown';
        return dataType.charAt(0).toUpperCase() + dataType.slice(1);
    };

    const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        } catch { return 'Invalid Date'; }
    };

    const getDataTypeBadgeValue = (dataType: string | null | undefined): 'stealer' | 'combolist' => {
        if (!dataType) return 'combolist';
        return dataType.toLowerCase() === 'stealer' ? 'stealer' : 'combolist';
    };

    useEffect(() => {
        if (!isOpen) {
            setShowPassword(false); setUnmaskedPassword(null); setUnmaskError(null);
            setRemainingUnmasks(null); setUnmaskSource(null); setWasAlreadyUnmasked(false);
            setIsSavingRecord(false);
        }
    }, [isOpen, threat?.originalIndex]);

    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate?.(currentIndex - 1);
            if (e.key === 'ArrowRight' && currentIndex < threats.length - 1) onNavigate?.(currentIndex + 1);
        };
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose, onNavigate, currentIndex, threats.length]);

    const copyToClipboard = async (text: string, field: string) => {
        if (!text || text === 'N/A') return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
            toast({ type: 'success', title: 'Copied', message: `${field.charAt(0).toUpperCase() + field.slice(1)} copied to clipboard` });
        } catch (err) {
            toast({ type: 'error', title: 'Copy Failed', message: 'Unable to copy to clipboard' });
        }
    };

    const handlePasswordToggle = async () => {
        if (!threat?.password) return;
        if (!unmaskedPassword && !showPassword) await handleUnmaskPassword();
        else setShowPassword(!showPassword);
    };

    const handleUnmaskPassword = async () => {
        if (context === 'search') {
            if (!fetchId || threat?.originalIndex === undefined) {
                setUnmaskError('Cannot unmask: Missing search context information'); return;
            }
            setUnmaskError(null);
            try {
                const response = await unmaskSearchPassword({ fetch_id: fetchId, result_index: threat.originalIndex }).unwrap();
                setUnmaskedPassword(response.password); setRemainingUnmasks(response.unmasks_remaining);
                setUnmaskSource(response.source); setWasAlreadyUnmasked(response.previously_unmasked);
                setShowPassword(true);
                toast({
                    type: 'success',
                    title: response.previously_unmasked ? 'Password Retrieved' : 'Password Unmasked',
                    message: `${response.unmasks_remaining} unmasks remaining`
                });
            } catch (error: any) {
                let errorMessage = 'Failed to unmask password. Please try again.';
                if (error.status === 403) errorMessage = 'Unmask quota exceeded or not supported in your plan';
                else if (error.status === 404) errorMessage = 'Record not found or fetch session expired';
                else if (error.data?.error) errorMessage = error.data.error;

                setUnmaskError(errorMessage);
                toast({ type: 'error', title: 'Unmask Failed', message: errorMessage });
            }
        } else {
            setUnmaskError('Unmasking saved incidents not implemented in this modal');
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handlePrevious = () => {
        if (currentIndex > 0) onNavigate?.(currentIndex - 1);
    };

    const handleNext = () => {
        if (currentIndex < threats.length - 1) onNavigate?.(currentIndex + 1);
    };

    const handleSaveThreat = async () => {
        if (!onSaveToThreats || !threat || isSavingRecord) return;
        setIsSavingRecord(true);
        try {
            await onSaveToThreats(threat);
        } catch (err) {
            console.error("Failed to commit node update profile:", err);
        } finally {
            setIsSavingRecord(false);
        }
    };

    if (!isOpen || !threat) return null;

    const hasSystemInfo = threat.hostname || threat.ip || threat.malware_path || threat.anti_virus;
    const priority = getPriority(threat.data_type);

    const DetailRow: React.FC<{
        label: string; value: string | null | undefined; icon: React.ReactNode;
        copyable?: boolean; fieldKey?: string; fallback?: string; isPassword?: boolean;
    }> = ({ label, value, icon, copyable = false, fieldKey = '', fallback = 'N/A', isPassword = false }) => {
        const displayValue = value || fallback;
        const isCopyable = copyable && value && value !== fallback;
        const actualValue = isPassword && unmaskedPassword ? unmaskedPassword : displayValue;
        const finalDisplayValue = isPassword && !showPassword && value ? '•'.repeat(Math.min(value.length, 20)) : actualValue;

        const getEyeConfig = () => {
            if (!isPassword) return null;
            if (!unmaskedPassword && !showPassword) {
                return { icon: <Eye className="h-3.5 w-3.5" />, tooltip: "Unmask password (uses quota)", loading: isUnmasking };
            } else {
                return { icon: showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />, tooltip: showPassword ? "Hide password" : "Show password", loading: false };
            }
        };

        const eyeConfig = getEyeConfig();

        return (
            <div className="space-y-1 py-3 border-b border-[#1A1D24] group">
                <div className="flex items-center gap-2 mb-1">
                    <div className="text-slate-500">{icon}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
                </div>
                <div className="flex items-center justify-between pl-6">
                    <div className="text-[13px] text-slate-300 font-mono break-all transition-colors group-hover:text-white" title={actualValue}>
                        {finalDisplayValue}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isPassword && value && eyeConfig && (
                            <button
                                onClick={handlePasswordToggle} disabled={eyeConfig.loading}
                                className="p-1.5 text-slate-400 hover:text-[#00FFFF] hover:bg-[#161A23] rounded transition-all disabled:opacity-50"
                                title={eyeConfig.tooltip}
                            >
                                {eyeConfig.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#00FFFF]" /> : eyeConfig.icon}
                            </button>
                        )}
                        {isCopyable && (
                            <button
                                onClick={() => copyToClipboard(actualValue || '', fieldKey)}
                                className="p-1.5 text-slate-400 hover:text-[#50A970] hover:bg-[#161A23] rounded transition-all"
                                title="Copy to clipboard"
                            >
                                {copiedField === fieldKey ? <CheckCircle2 className="h-3.5 w-3.5 text-[#50A970]" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const modalContent = (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={handleOverlayClick}>
            <div ref={modalRef} className="relative w-full max-w-4xl max-h-[90vh] bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-fade-in font-mono">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[#1A1D24] bg-[#12151C] flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h2 className="text-[14px] font-bold text-white tracking-widest uppercase flex items-center gap-2">
                            <Terminal className="w-5 h-5 text-[#50A970]" />
                            Threat Intelligence Details
                        </h2>
                        <div className="flex gap-2 items-center">
                            <PriorityBadge priority={priority} />
                            <DataTypeBadge dataType={getDataTypeBadgeValue(threat.data_type)} />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {threats.length > 1 && (
                            <div className="flex items-center gap-2 bg-[#0A0B0E] border border-[#1A1D24] rounded-lg px-2 py-1">
                                <button
                                    onClick={handlePrevious} disabled={currentIndex === 0}
                                    className="p-1 text-slate-500 hover:text-white disabled:opacity-30 transition-colors"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="text-[10px] text-slate-500 tracking-widest uppercase">
                                    <strong className="text-white">{currentIndex + 1}</strong> OF {threats.length}
                                </span>
                                <button
                                    onClick={handleNext} disabled={currentIndex === threats.length - 1}
                                    className="p-1 text-slate-500 hover:text-white disabled:opacity-30 transition-colors"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        {/* ✅ REMOVED CRASH GATE: Lock condition has been removed; button relies strictly on method presence now */}
                        {onSaveToThreats ? (
                            <button 
                                onClick={handleSaveThreat} 
                                disabled={isSavingRecord}
                                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-[#50A970] text-black px-4 py-2 rounded-md hover:bg-[#3d8556] transition-colors min-w-[110px] justify-center disabled:opacity-50"
                            >
                                {isSavingRecord ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Bookmark className="h-3.5 w-3.5" />
                                )} 
                                {isSavingRecord ? 'SAVING...' : 'Save Node'}
                            </button>
                        ) : (
                            <button disabled className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-transparent border border-[#1A1D24] text-slate-500 px-4 py-2 rounded-md opacity-50 cursor-not-allowed">
                                <Lock className="h-3.5 w-3.5" /> Save Node
                            </button>
                        )}

                        <button onClick={onClose} className="p-2 text-slate-500 hover:text-[#FF3333] hover:bg-[#161A23] rounded-lg transition-all ml-2 border border-transparent hover:border-[#1A1D24]">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content Wrapper */}
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 min-h-0 overflow-hidden">

                        {/* Left Column - Main Details */}
                        <div className="lg:col-span-2 p-6 overflow-y-auto custom-scrollbar space-y-6 h-full">

                            <div>
                                <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24]">Core Identifiers</h3>
                                <div className="space-y-1">
                                    <DetailRow label="URL / Domain Target" value={threat.url} icon={<Globe className="h-3.5 w-3.5" />} copyable fieldKey="url" />
                                    <DetailRow label="Extracted Username" value={threat.username} icon={<User className="h-3.5 w-3.5" />} copyable fieldKey="username" />
                                    <DetailRow label="Obfuscated Password" value={threat.password} icon={<Key className="h-3.5 w-3.5" />} copyable fieldKey="password" isPassword={true} />
                                    <DetailRow label="Capture Timestamp" value={formatDate(threat.log_date)} icon={<Calendar className="h-3.5 w-3.5" />} />
                                </div>
                            </div>

                            {/* Terminal block for unmasking status */}
                            {threat.password && (unmaskError || unmaskedPassword) && (
                                <div className="bg-[#050505] border border-[#1A1D24] rounded-lg p-4 space-y-2">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                        <Terminal className="h-3 w-3" /> Terminal Output
                                    </div>
                                    {unmaskError && (
                                        <div className="flex items-center gap-2 text-[11px] text-[#FF3333] bg-[#FF3333]/10 border border-[#FF3333]/30 px-3 py-2 rounded">
                                            <AlertTriangle className="h-3.5 w-3.5" />
                                            [ERROR] {unmaskError}
                                        </div>
                                    )}
                                    {unmaskedPassword && remainingUnmasks !== null && (
                                        <div className="flex flex-col gap-1 text-[11px] text-[#50A970] bg-[#50A970]/10 border border-[#50A970]/30 px-3 py-2 rounded">
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                [SUCCESS] Password {wasAlreadyUnmasked ? 'retrieved from cache' : 'unmasked via API'}.
                                            </div>
                                            <div className="pl-5 text-slate-500">
                                                Quota remaining: <span className="text-white">{remainingUnmasks}</span> {unmaskSource && `| Engine: ${unmaskSource}`}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {hasSystemInfo && (
                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-300 tracking-widest uppercase mb-4 pb-2 border-b border-[#1A1D24] mt-8">System Fingerprint</h3>
                                    <div className="space-y-1">
                                        {threat.hostname && <DetailRow label="Infected Hostname" value={threat.hostname} icon={<Monitor className="h-3.5 w-3.5" />} copyable fieldKey="hostname" />}
                                        {threat.ip && <DetailRow label="Origin IP Address" value={threat.ip} icon={<MapPin className="h-3.5 w-3.5" />} copyable fieldKey="ip" />}
                                        {threat.malware_path && <DetailRow label="Malware Execution Path" value={threat.malware_path} icon={<FileText className="h-3.5 w-3.5" />} copyable fieldKey="malware_path" />}
                                        {threat.anti_virus && <DetailRow label="Detected Antivirus" value={threat.anti_virus} icon={<Shield className="h-3.5 w-3.5" />} copyable fieldKey="anti_virus" />}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Metadata */}
                        <div className="bg-[#12151C] p-6 border-t lg:border-t-0 lg:border-l border-[#1A1D24] overflow-y-auto custom-scrollbar h-full">
                            <div className="space-y-8">

                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                        <Database className="h-3.5 w-3.5 text-[#00FFFF]" /> Node Metadata
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Index ID</div>
                                            <div className="text-[12px] text-slate-300">#{threat.originalIndex}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Log ID</div>
                                            <div className="text-[12px] text-slate-300">{threat.log_id || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Data Origin Type</div>
                                            <div className="text-[12px] text-[#00FFFF]">{formatDataType(threat.data_type)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Source Endpoint</div>
                                            <div className="text-[12px] text-slate-400 break-all">{threat._source_endpoint || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Location Mapping</div>
                                            <div className="text-[12px] text-purple-400 font-bold">{threat.country || threat.country_code || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                        <Shield className="h-3.5 w-3.5 text-orange-500" /> Assessment
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Severity Level</div>
                                            <PriorityBadge priority={priority} />
                                        </div>
                                        <div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">System Status</div>
                                            <div className="inline-block px-2.5 py-1 rounded border border-[#FF3333]/30 bg-[#FF3333]/10 text-[#FF3333] text-[10px] font-bold tracking-widest">
                                                ACTION REQUIRED
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {context === 'search' && fetchId && (
                                    <div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                            <Network className="h-3.5 w-3.5 text-purple-500" /> Session Link
                                        </h3>
                                        <div className="space-y-2">
                                            <div>
                                                <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">Fetch ID</div>
                                                <div className="text-[11px] text-slate-400 font-mono bg-[#0A0B0E] border border-[#1A1D24] px-2 py-1.5 rounded break-all">
                                                    {fetchId}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default SearchResultModal;